import { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Bell, X } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageProvider";
import { Sentry } from "@/lib/sentry";
import { PUSH_OPTIN_EVENT } from "@/lib/pushOptinEvents";

interface PushOptInSheetProps {
  open: boolean;
  onClose: () => void;
  onAccept: () => Promise<void> | void;
  onDecline: () => Promise<void> | void;
  // Origin of the soft-ask, for funnel telemetry (e.g. "in_session" | "mount" |
  // "checkin"). Tagged onto push_optin_shown so we can compare which placement
  // converts. The granted/decline outcomes are logged by the caller (the native
  // prompt result is only known there).
  trigger?: string;
}

export function PushOptInSheet({ open, onClose, onAccept, onDecline, trigger }: PushOptInSheetProps) {
  const { t } = useLanguage();

  useEffect(() => {
    if (open) {
      try {
        Sentry.captureMessage(PUSH_OPTIN_EVENT.shown, {
          level: "info",
          tags: { feature: "push_optin", ...(trigger ? { trigger } : {}) },
        });
      } catch {
        /* Sentry not initialized in dev */
      }
    }
  }, [open, trigger]);

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[60]"
            style={{ background: "rgba(0,0,0,0.65)" }}
            onClick={onClose}
          />
          <motion.div
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ type: "spring", damping: 28, stiffness: 300 }}
            className="fixed bottom-0 left-0 right-0 z-[61] flex justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="w-full max-w-[440px] rounded-t-3xl px-6 pt-5"
              style={{
                background: "linear-gradient(180deg, hsl(220 35% 9%), hsl(222 38% 7%))",
                border: "1px solid hsl(187 72% 50% / 0.20)",
                borderBottom: "none",
                paddingBottom: "max(1.75rem, env(safe-area-inset-bottom))",
              }}
            >
              <div className="w-10 h-1 rounded-full mx-auto mb-3" style={{ background: "hsl(218 14% 30%)" }} />
              <div className="flex items-start gap-3 mb-4">
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                  style={{
                    background: "hsl(187 72% 50% / 0.15)",
                    border: "1px solid hsl(187 72% 50% / 0.30)",
                    boxShadow: "0 0 16px hsl(187 72% 50% / 0.20)",
                  }}
                >
                  <Bell size={16} style={{ color: "hsl(187 72% 60%)" }} />
                </div>
                <div className="flex-1">
                  <h2 className="font-display text-[17px] font-semibold text-white leading-snug">
                    {t("push.optin.title")}
                  </h2>
                </div>
                <button
                  onClick={onClose}
                  className="w-8 h-8 rounded-xl flex items-center justify-center shrink-0"
                  style={{ background: "hsl(218 14% 18%)", border: "1px solid hsl(218 14% 25%)" }}
                  aria-label={t("push.optin.cta.decline")}
                >
                  <X size={13} style={{ color: "hsl(218 14% 60%)" }} />
                </button>
              </div>
              <p
                className="text-[13px] font-body leading-relaxed mb-2"
                style={{ color: "hsl(218 14% 78%)" }}
              >
                {t("push.optin.subtitle")}
              </p>
              <p
                className="text-[11px] font-body leading-relaxed mb-5"
                style={{ color: "hsl(218 14% 55%)" }}
              >
                {t("push.optin.note")}
              </p>
              <div className="flex flex-col gap-2">
                <button
                  type="button"
                  onClick={() => { void onAccept(); }}
                  className="w-full py-3.5 rounded-xl font-display text-[14px] font-semibold tracking-wide transition-all active:scale-[0.98]"
                  style={{
                    background: "linear-gradient(135deg, hsl(187 60% 35%), hsl(187 72% 45%))",
                    color: "white",
                    boxShadow: "0 4px 24px hsl(187 72% 50% / 0.25)",
                  }}
                >
                  {t("push.optin.cta.accept")}
                </button>
                <button
                  type="button"
                  onClick={() => { void onDecline(); }}
                  className="w-full py-3 rounded-xl font-display text-[13px] font-medium tracking-wide transition-all active:scale-[0.98]"
                  style={{
                    background: "transparent",
                    border: "1px solid hsl(218 14% 25%)",
                    color: "hsl(218 14% 70%)",
                  }}
                >
                  {t("push.optin.cta.decline")}
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

export default PushOptInSheet;
